import dayjs from "dayjs";
import { type RefObject, useEffect, useMemo, useState } from "react";
import type {
  RadioTranscriptResponse,
  RadioTranscriptSegment,
} from "src/store/api/generatedApi";

// Keep a word highlighted briefly past its end so the highlight bridges the
// short silences between words instead of flickering off between them.
const ACTIVE_WORD_GRACE_MS = 400;
// Word highlighting doesn't need per-frame precision; sample a few times a
// second so playback doesn't drive 60 re-renders/sec through the transcript.
const PLAYHEAD_SAMPLE_MS = 200;

export interface TranscriptWordView {
  key: string;
  text: string;
  startMs: number;
  endMs: number;
  sourceId: number | null;
  concurrentTransmissions: number;
}

export interface TranscriptLineView {
  key: string;
  startMs: number;
  language: string | null;
  receiverId: number | null;
  channelLabel: string | null;
  words: TranscriptWordView[];
}

// Convert API segments (word offsets relative to the segment) into lines whose
// word timings are absolute epoch ms, so they align to the player's wall-clock
// playhead (hls.js playingDate). Channels share every start time, so the receiver
// is part of each key — without it every feed collides on one key per instant.
export const flattenTranscript = (
  segments: readonly RadioTranscriptSegment[],
  channelLabels: ReadonlyMap<number, string> = new Map()
): TranscriptLineView[] =>
  segments.map((segment) => {
    const segmentStartMs = dayjs(segment.segment_start).valueOf();
    const receiverId = segment.receiver_id ?? null;
    const key = `${receiverId ?? "none"}-${segmentStartMs}`;
    return {
      key,
      startMs: segmentStartMs,
      language: segment.language ?? null,
      receiverId,
      channelLabel:
        receiverId == null ? null : (channelLabels.get(receiverId) ?? null),
      words: segment.words.map((word, index) => ({
        key: `${key}-${index}`,
        text: word.text,
        startMs: segmentStartMs + word.start_ms,
        endMs: segmentStartMs + word.end_ms,
        sourceId: word.source_id,
        concurrentTransmissions: word.concurrent_transmissions,
      })),
    };
  });

export interface TranscriptChannelGroup {
  key: string;
  receiverId: number | null;
  channelLabel: string | null;
  lines: TranscriptLineView[];
}

// One block per channel, so each talkgroup's traffic reads as its own thread
// rather than every channel's transmissions interleaved on one clock. A clip is
// recorded per talkgroup, so a block is the transcript of that channel's clips
// and nothing else. Channels hold the order they first speak in the window, and
// lines hold their time order inside a channel, so a block still reads the way
// the traffic happened.
export const groupLinesByChannel = (
  lines: readonly TranscriptLineView[]
): TranscriptChannelGroup[] => {
  const groups = new Map<string, TranscriptChannelGroup>();
  for (const line of lines) {
    const key = String(line.receiverId ?? "none");
    const group = groups.get(key);
    if (group) group.lines.push(line);
    else
      groups.set(key, {
        key,
        receiverId: line.receiverId,
        channelLabel: line.channelLabel,
        lines: [line],
      });
  }
  return [...groups.values()];
};

export interface TranscriptSpeakerRun {
  key: string;
  sourceId: number | null;
  concurrentTransmissions: number;
  words: TranscriptWordView[];
}

// A line split into runs of consecutive words from one handset. A handset that
// speaks either side of another gets a run each time, so the exchange stays
// legible. Words several handsets talked over become their own run, so shared
// audio never reads as one handset speaking.
export const groupWordsBySpeaker = (
  words: readonly TranscriptWordView[]
): TranscriptSpeakerRun[] => {
  const runs: TranscriptSpeakerRun[] = [];
  for (const word of words) {
    const current = runs.at(-1);
    if (
      current?.sourceId === word.sourceId &&
      current.concurrentTransmissions === word.concurrentTransmissions
    )
      current.words.push(word);
    else
      runs.push({
        key: word.key,
        sourceId: word.sourceId,
        concurrentTransmissions: word.concurrentTransmissions,
        words: [word],
      });
  }
  return runs;
};

// The word under the playhead: the most recent word whose start has passed, kept
// until shortly after its end so inter-word gaps don't unhighlight. Only the
// played channel's lines count — another channel's word is under the playhead
// just as often, and the listener cannot hear it.
export const findActiveWordKey = (
  lines: readonly TranscriptLineView[],
  playheadMs: number | null,
  playingReceiverId: number | null
): string | null => {
  if (playheadMs == null) return null;
  let active: TranscriptWordView | null = null;
  for (const line of lines) {
    if (playingReceiverId != null && line.receiverId !== playingReceiverId)
      continue;
    for (const word of line.words) {
      if (word.startMs <= playheadMs) active = word;
      else
        return active && playheadMs < active.endMs + ACTIVE_WORD_GRACE_MS
          ? active.key
          : null;
    }
  }
  return active && playheadMs < active.endMs + ACTIVE_WORD_GRACE_MS
    ? active.key
    : null;
};

// Track the player's wall-clock playhead (epoch ms) while it plays, sampled on
// an interval and on seek/metadata so the active-word highlight follows along.
export const usePlayheadMs = (
  getPlayingDate: () => Date | null,
  mediaRef: RefObject<HTMLMediaElement | null>
): number | null => {
  const [playheadMs, setPlayheadMs] = useState<number | null>(null);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    let intervalId: number | null = null;
    const sample = () => {
      const date = getPlayingDate();
      setPlayheadMs(date ? date.getTime() : null);
    };
    const start = () => {
      if (intervalId != null) return;
      sample();
      intervalId = window.setInterval(sample, PLAYHEAD_SAMPLE_MS);
    };
    const stop = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      sample();
    };

    media.addEventListener("playing", start);
    media.addEventListener("pause", stop);
    media.addEventListener("ended", stop);
    media.addEventListener("seeked", sample);
    media.addEventListener("loadedmetadata", sample);
    if (!media.paused) start();

    return () => {
      media.removeEventListener("playing", start);
      media.removeEventListener("pause", stop);
      media.removeEventListener("ended", stop);
      media.removeEventListener("seeked", sample);
      media.removeEventListener("loadedmetadata", sample);
      if (intervalId != null) clearInterval(intervalId);
    };
  }, [getPlayingDate, mediaRef]);

  return playheadMs;
};

// Bind a fetched transcript to a media element: flatten its segments to
// absolute-timed lines and track which word sits under the playhead. Shared by
// the live and historical panels so their sync logic can't drift apart.
export const useTranscriptSync = (
  transcript: RadioTranscriptResponse | undefined,
  getPlayingDate: () => Date | null,
  mediaRef: RefObject<HTMLMediaElement | null>,
  {
    channelLabels,
    playingReceiverId,
  }: {
    channelLabels: ReadonlyMap<number, string>;
    playingReceiverId: number | null;
  }
): { lines: TranscriptLineView[]; activeWordKey: string | null } => {
  const lines = useMemo(
    () => flattenTranscript(transcript?.segments ?? [], channelLabels),
    [transcript, channelLabels]
  );
  const playheadMs = usePlayheadMs(getPlayingDate, mediaRef);
  const activeWordKey = useMemo(
    () => findActiveWordKey(lines, playheadMs, playingReceiverId),
    [lines, playheadMs, playingReceiverId]
  );
  return { lines, activeWordKey };
};
